// SB6-AUTH-CONSISTENCY-01 — Account-section field wrapper.
//
// Christian wants the Account Settings widgets to look like the
// template-page form widgets — consistent height + label-on-top style
// across every field on every section (Profile / Contact / Address /
// Preferences / Security / Privacy).
//
// Decision (a) from the SB6 bucket: keep MUI <TextField> / <Select>
// but normalise their chrome. This primitive is a thin pass-through
// around <TextField> that applies the canonical:
//
//   - `size="small"` — matches the toolbar primitives (SearchField,
//     SingleSelectFilter, …).
//   - `slotProps.inputLabel.shrink = true` — label always sits above the
//     field. Avoids the floating-label-on-click jitter Christian
//     flagged at [00:18:26] (First Name vs Last Name inconsistency).
//   - `fullWidth` by default — caller can override.
//   - `variant="outlined"` — matches the rest of the app.
//
// Use this for every text input AND every select (the <select> prop is
// a passthrough on MUI's TextField).
import { TextField, type TextFieldProps } from "@mui/material";
import { forwardRef, type ReactNode } from "react";

export type AccountFieldProps = Omit<TextFieldProps, "size" | "variant"> & {
  /** Optional explicit override; defaults to "outlined". */
  variant?: TextFieldProps["variant"];
  /** Optional children — MenuItems for <Select> mode. */
  children?: ReactNode;
};

/**
 * Thin <TextField> wrapper that applies the canonical Account-section
 * chrome — small density, outlined variant, always-shrunk label, full
 * width by default.
 */
export const AccountField = forwardRef<HTMLDivElement, AccountFieldProps>(
  function AccountField(
    { slotProps, fullWidth = true, variant = "outlined", children, ...rest },
    ref,
  ) {
    // MUI v6 typing allows slotProps.inputLabel to be either a plain
    // object OR a callback that returns one. Only spread when it's an
    // object literal; otherwise fall back to the bare `{ shrink: true }`
    // so the always-on-top label rule still applies (the caller's
    // callback case is rare enough to override explicitly).
    const existingInputLabel = slotProps?.inputLabel;
    const inputLabelOverride =
      existingInputLabel && typeof existingInputLabel === "object"
        ? { shrink: true, ...existingInputLabel }
        : { shrink: true };
    return (
      <TextField
        ref={ref}
        size="small"
        variant={variant}
        fullWidth={fullWidth}
        slotProps={{
          ...slotProps,
          inputLabel: inputLabelOverride,
        }}
        {...rest}
      >
        {children}
      </TextField>
    );
  },
);
